import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/axios';
import { filterPhone, isValidEmail } from '../utils/inputValidation';
import { getPlaceholders } from '../utils/placeholders';

const CSS = `
.lp {
  --accent: #2563eb;
  --accent-2: #4f46e5;
  --blue-500: #3b82f6;
  --indigo-500: #6366f1;
  --night: #0b1955;
  --ink: #0f172a;
  --muted: #64748b;
  --line: #e9eef5;
  --bg: #f6f8fc;
  --card: #fff;
  --brand: linear-gradient(150deg, #0b1955 0%, #1a3496 45%, #1f58d4 100%);
  --btn: linear-gradient(135deg, var(--accent), var(--accent-2));
  background: var(--bg);
  color: var(--ink);
  font-family: ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  font-size: 16px;
  line-height: 1.6;
  min-height: 100vh;
  overflow-x: hidden;
}
.lp *, .lp *::before, .lp *::after { box-sizing: border-box; }
.lp h1, .lp h2, .lp h3 { margin: 0; font-weight: 900; letter-spacing: -0.025em; line-height: 1.08; }
.lp p { margin: 0; }
.lp :focus-visible { outline: 3px solid var(--indigo-500); outline-offset: 3px; border-radius: 8px; }
.lp-hero :focus-visible, .lp-night :focus-visible, .lp-foot :focus-visible { outline-color: #fff; }
.lp-wrap { max-width: 1160px; margin: 0 auto; padding: 0 24px; }

/* Logo */
.lp-logo { display: inline-flex; align-items: center; gap: 9px; font-weight: 900; font-size: 21px; letter-spacing: -0.03em; line-height: 1; text-decoration: none; color: var(--ink); }
.lp-logo-mark { width: 34px; height: 34px; border-radius: 10px; background: var(--btn); color: #fff; display: grid; place-items: center; flex: 0 0 auto; box-shadow: 0 6px 14px rgba(37,99,235,.35); }
.lp-logo b { font-weight: 900; background: linear-gradient(135deg,#2563eb,#4f46e5); -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; color: #2563eb; }
.lp-hero .lp-logo { color: #fff; }
.lp-hero .lp-logo b { background: none; -webkit-text-fill-color: #bfdbfe; color: #bfdbfe; }
.lp-hero .lp-logo-mark { background: #fff; color: #1a3496; }

/* Boutons */
.lp-btn {
  display: inline-flex; align-items: center; justify-content: center; min-height: 50px; padding: 0 28px;
  border-radius: 14px; font: 800 15px/1 ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif;
  text-decoration: none; cursor: pointer; border: 1px solid transparent; transition: transform .15s, box-shadow .2s;
}
.lp-btn-primary { background: var(--btn); color: #fff; box-shadow: 0 8px 20px rgba(37,99,235,.35); }
.lp-btn-primary:hover { box-shadow: 0 10px 26px rgba(37,99,235,.5); }
.lp-btn-ghost { background: #fff; color: #334155; border-color: #cbd5e1; }
.lp-btn-ghost:hover { background: #f1f5f9; }
.lp-btn[disabled] { opacity: .65; cursor: not-allowed; }
.lp-hero .lp-btn-primary { background: #fff; color: #1a3496; box-shadow: 0 10px 26px rgba(0,0,0,.28); }
.lp-hero .lp-btn-ghost { background: transparent; color: #fff; border-color: rgba(255,255,255,.55); }
.lp-hero .lp-btn-ghost:hover { background: rgba(255,255,255,.12); }

/* Ligne de coupe (séparateur signature salon) */
.lp-cut { position: relative; display: flex; align-items: center; justify-content: center; height: 34px; margin: 6px 0; color: var(--accent); }
.lp-cut::before { content: ''; position: absolute; left: 0; right: 0; top: 50%; border-top: 2px dashed #c4d2e8; }
.lp-cut svg { position: relative; background: var(--bg); padding: 0 16px; }
.lp-night .lp-cut { color: #fff; }
.lp-night .lp-cut::before { border-color: rgba(255,255,255,.3); }
.lp-night .lp-cut svg { background: transparent; }

/* Accent de titre : petits ciseaux */
.lp-snip { display: inline-flex; color: var(--accent); margin-bottom: 14px; }
.lp-night .lp-snip, .lp-hero .lp-snip { color: #bfdbfe; }

/* Hero */
.lp-hero { background: var(--brand); color: #fff; position: relative; overflow: hidden; }
.lp-hero::before { content: ''; position: absolute; top: -90px; right: -70px; width: 380px; height: 380px; border-radius: 50%; background: radial-gradient(circle, rgba(99,102,241,.35) 0%, transparent 70%); pointer-events: none; }
.lp-hero::after { content: ''; position: absolute; bottom: -90px; left: -70px; width: 320px; height: 320px; border-radius: 50%; background: radial-gradient(circle, rgba(59,130,246,.3) 0%, transparent 70%); pointer-events: none; }
.lp-nav { position: relative; z-index: 1; display: flex; align-items: center; justify-content: space-between; padding: 22px 0; gap: 16px; }
.lp-nav-links { display: flex; align-items: center; gap: 24px; }
.lp-nav-links a { color: #dbeafe; font-weight: 700; font-size: 14px; text-decoration: none; }
.lp-nav-links a:hover { color: #fff; text-decoration: underline; }
.lp-hero-grid { position: relative; z-index: 1; display: grid; grid-template-columns: 1fr 1.05fr; gap: 56px; align-items: center; padding: 44px 0 92px; }
.lp-hero h1 { font-size: clamp(34px, 5vw, 58px); font-weight: 900; }
.lp-lead { margin-top: 22px; font-size: 18px; color: #dbeafe; max-width: 520px; }
.lp-cta { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 32px; }
.lp-hero-note { margin-top: 18px; font-size: 14px; color: #bfdbfe; font-weight: 600; display: inline-flex; align-items: center; gap: 8px; }

/* Captures */
.lp-shot { margin: 0; position: relative; background: #fff; border-radius: 22px; overflow: hidden; border: 1px solid rgba(241,245,249,.8); box-shadow: 0 30px 70px rgba(0,0,0,.4), 0 6px 18px rgba(0,0,0,.2); min-width: 0; }
.lp-shot-bar { display: flex; align-items: center; gap: 6px; padding: 10px 14px; background: #f1f5f9; border-bottom: 1px solid #e2e8f0; }
.lp-shot-bar i { width: 9px; height: 9px; border-radius: 50%; background: #cbd5e1; }
.lp-shot-bar span { margin-left: 8px; flex: 1; max-width: 260px; height: 20px; border-radius: 9px; background: #fff; border: 1px solid #e2e8f0; font-size: 10.5px; line-height: 18px; padding: 0 10px; color: #64748b; font-weight: 600; overflow: hidden; white-space: nowrap; }
.lp-shot img { display: block; width: 100%; max-width: 100%; height: auto; }
.lp-shots { display: grid; grid-template-columns: repeat(2, 1fr); gap: 28px; margin-top: 40px; }
.lp-shots .lp-shot { box-shadow: 0 16px 40px rgba(15,23,42,.14), 0 3px 10px rgba(15,23,42,.06); border-radius: 18px; }
.lp-shots figure { margin: 0; min-width: 0; }
.lp-shots .lp-big { grid-column: span 2; }
.lp-shots figcaption { margin-top: 14px; font-weight: 800; font-size: 16px; letter-spacing: -0.01em; }
.lp-shots figcaption small { display: block; font-weight: 400; color: var(--muted); font-size: 14px; margin-top: 2px; }
@media (prefers-reduced-motion: reduce) { .lp-btn { transition: none; } }

/* Sections */
.lp-sec { padding: 80px 0; }
.lp-sec h2 { font-size: clamp(27px, 3.6vw, 40px); max-width: 720px; }
.lp-sec-intro { margin-top: 14px; color: var(--muted); max-width: 620px; font-size: 18px; }
.lp-about { display: grid; grid-template-columns: 1fr 1fr; gap: 56px; align-items: start; }
.lp-about p { font-size: 18px; color: var(--muted); }
.lp-about p + p { margin-top: 16px; }
.lp-about strong { color: var(--ink); }

/* Fonctionnalités */
.lp-feats { display: grid; grid-template-columns: repeat(12, 1fr); gap: 16px; margin-top: 40px; }
.lp-f { padding: 26px; background: #fff; border: 1px solid var(--line); border-radius: 20px; box-shadow: 0 4px 16px rgba(15,23,42,.05); grid-column: span 4; }
.lp-f-ico { width: 46px; height: 46px; border-radius: 13px; display: grid; place-items: center; background: #eaf1ff; color: var(--accent); margin-bottom: 16px; }
.lp-f h3 { font-size: 20px; font-weight: 900; }
.lp-f p { margin-top: 8px; color: var(--muted); font-size: 15px; }
.lp-f-wide { grid-column: span 8; background: var(--brand); color: #fff; border: 0; padding: 34px; box-shadow: 0 20px 44px rgba(26,52,150,.32); }
.lp-f-wide .lp-f-ico { background: rgba(255,255,255,.16); color: #fff; }
.lp-f-wide h3 { font-size: 26px; }
.lp-f-wide p { color: #dbeafe; max-width: 460px; font-size: 16px; }
.lp-chips { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 20px; }
.lp-chips span { padding: 7px 14px; border-radius: 11px; font-size: 13px; font-weight: 800; background: rgba(255,255,255,.14); border: 1px solid rgba(255,255,255,.28); }
.lp-f-dark { background: #0f172a; color: #fff; border: 0; }
.lp-f-dark .lp-f-ico { background: rgba(255,255,255,.1); color: #fff; }
.lp-f-dark p { color: #cbd5e1; }
.lp-f-soft { background: #eaf1ff; border-color: #dbe6fb; }

/* Deux devises */
.lp-night { background: var(--brand); color: #fff; }
.lp-night .lp-sec-intro { color: #dbeafe; }
.lp-markets { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-top: 40px; }
.lp-market { background: rgba(255,255,255,.08); border: 1px solid rgba(255,255,255,.18); border-radius: 24px; padding: 30px; }
.lp-market-h { display: flex; align-items: center; gap: 12px; }
.lp-market-h svg { color: #bfdbfe; flex: 0 0 auto; }
.lp-market h3 { font-size: 24px; }
.lp-market > p { margin-top: 8px; color: #bfdbfe; }
.lp-market dl { margin: 18px 0 0; }
.lp-market dt { font-weight: 800; margin-top: 16px; }
.lp-market dd { margin: 2px 0 0; color: #dbeafe; font-size: 15px; }

/* Étapes */
.lp-steps { list-style: none; margin: 40px 0 0; padding: 0; display: grid; gap: 14px; max-width: 840px; }
.lp-steps li { display: grid; grid-template-columns: 54px 1fr; gap: 18px; align-items: start; background: #fff; border: 1px solid var(--line); border-radius: 20px; padding: 22px; box-shadow: 0 4px 16px rgba(15,23,42,.05); }
.lp-step-n { width: 54px; height: 54px; border-radius: 16px; background: var(--btn); color: #fff; font-size: 22px; font-weight: 900; display: grid; place-items: center; box-shadow: 0 6px 16px rgba(37,99,235,.35); }
.lp-steps h3 { font-size: 20px; }
.lp-steps p { margin-top: 4px; color: var(--muted); }

/* Contact */
.lp-contact { background: #fff; border-top: 1px solid var(--line); }
.lp-contact-grid { display: grid; grid-template-columns: .9fr 1.1fr; gap: 56px; align-items: start; }
.lp-form { position: relative; background: #fff; border-radius: 26px; padding: 32px; border: 1px solid var(--line); box-shadow: 0 20px 60px rgba(15,23,42,.12), 0 4px 16px rgba(15,23,42,.06); }
.lp-field { display: flex; flex-direction: column; margin-bottom: 16px; }
.lp-field label { font-weight: 800; font-size: 14px; margin-bottom: 6px; }
.lp-field label small { font-weight: 600; color: var(--muted); font-size: 13px; }
.lp-field input, .lp-field textarea { font: 600 15px ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif; color: var(--ink); background: var(--bg); border: 1.5px solid #cbd5e1; border-radius: 14px; padding: 12px 14px; width: 100%; }
.lp-field textarea { min-height: 110px; resize: vertical; }
.lp-field input::placeholder, .lp-field textarea::placeholder { color: #64748b; font-weight: 400; }
.lp-field input[aria-invalid="true"] { border-color: #dc2626; }
.lp-err { color: #dc2626; font-size: 13px; font-weight: 700; margin-top: 6px; }
.lp-form-err { background: #fef2f2; color: #dc2626; border: 1px solid #fee2e2; border-radius: 14px; padding: 12px 14px; margin-bottom: 16px; font-weight: 700; font-size: 14px; }
.lp-ok h3 { font-size: 24px; }
.lp-ok p { margin-top: 10px; color: var(--muted); }

/* Footer */
.lp-foot { background: #0f172a; color: #fff; padding: 44px 0; }
.lp-foot-in { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; flex-wrap: wrap; }
.lp-foot .lp-logo { color: #fff; }
.lp-foot .lp-logo b { -webkit-text-fill-color: #93c5fd; color: #93c5fd; background: none; }
.lp-foot p { color: #94a3b8; margin-top: 12px; font-size: 14px; }
.lp-foot nav { display: flex; gap: 24px; flex-wrap: wrap; }
.lp-foot nav a { color: #e2e8f0; font-weight: 700; font-size: 14px; }

@media (max-width: 960px) {
  .lp-hero-grid, .lp-about, .lp-contact-grid { grid-template-columns: 1fr; gap: 40px; }
  .lp-hero-grid { padding-bottom: 64px; }
  .lp-f, .lp-f-wide { grid-column: span 6; }
  .lp-markets { grid-template-columns: 1fr; }
  .lp-sec { padding: 58px 0; }
}
@media (max-width: 600px) {
  .lp-wrap { padding: 0 18px; }
  .lp-f, .lp-f-wide { grid-column: span 12; }
  .lp-nav-links a.hide-sm { display: none; }
  .lp-cta .lp-btn { width: 100%; }
  .lp-shots { grid-template-columns: 1fr; gap: 24px; }
  .lp-shots .lp-big { grid-column: span 1; }
  .lp-steps li { grid-template-columns: 44px 1fr; gap: 12px; padding: 16px; }
  .lp-step-n { width: 44px; height: 44px; font-size: 19px; border-radius: 13px; }
  .lp-form { padding: 22px; }
  .lp-market { padding: 22px; }
}
`;

/* Icônes (univers salon) */
const I = {
  scissors: (s = 24) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="6" cy="6" r="3" /><circle cx="6" cy="18" r="3" />
      <line x1="20" y1="4" x2="8.12" y2="15.88" /><line x1="14.47" y1="14.48" x2="20" y2="20" /><line x1="8.12" y1="8.12" x2="12" y2="12" />
    </svg>
  ),
  comb: (s = 24) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 8h18" /><path d="M5 8v5M8.5 8v8M12 8v5M15.5 8v8M19 8v5" />
    </svg>
  ),
  chart: (s = 24) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 19V5" /><path d="M4 15l4-4 4 3 7-7" /><path d="M16 7h4v4" />
    </svg>
  ),
  calendar: (s = 24) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="5" width="18" height="16" rx="3" /><path d="M3 9h18M8 3v4M16 3v4" /><circle cx="12" cy="15" r="1.4" fill="currentColor" stroke="none" />
    </svg>
  ),
  bag: (s = 24) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 8h14l-1 12H6L5 8Z" /><path d="M9 8a3 3 0 0 1 6 0" />
    </svg>
  ),
  card: (s = 24) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="6" width="18" height="12" rx="3" /><path d="M3 10h18M7 15h4" />
    </svg>
  ),
  user: (s = 24) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="8" r="4" /><path d="M4 20a8 8 0 0 1 16 0" />
    </svg>
  ),
  clock: (s = 24) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
    </svg>
  ),
  star: (s = 24) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.9 6.8 19.2l1-5.8L3.5 9.2l5.9-.9L12 3Z" />
    </svg>
  ),
  bell: (s = 24) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 9a6 6 0 0 1 12 0c0 5 2 6 2 6H4s2-1 2-6Z" /><path d="M10 20a2 2 0 0 0 4 0" />
    </svg>
  ),
  coins: (s = 24) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <ellipse cx="12" cy="6" rx="7" ry="3" /><path d="M5 6v6c0 1.7 3.1 3 7 3s7-1.3 7-3V6" /><path d="M5 12v6c0 1.7 3.1 3 7 3s7-1.3 7-3v-6" />
    </svg>
  ),
  euro: (s = 24) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17 5.5A7 7 0 1 0 17 18.5" /><path d="M4 10h9M4 14h9" />
    </svg>
  ),
};

const FEATURES = [
  { k: 'wide', cls: 'lp-f lp-f-wide', ico: I.scissors(26), title: 'Une caisse rapide comme un coup de ciseaux',
    text: "Choisissez la prestation, ajoutez un produit, encaissez. Le reçu part et la vente est rattachée à la cliente et à la coiffeuse.",
    chips: ['Espèces', 'Wave', 'Orange Money', 'Carte'] },
  { k: 'fin', cls: 'lp-f lp-f-soft', ico: I.chart(24), title: 'Finances et statistiques',
    text: 'Chiffre d\'affaires, dépenses, prestations les plus demandées. Vos rapports sont prêts quand le comptable les réclame.' },
  { k: 'rdv', cls: 'lp-f', ico: I.calendar(24), title: 'Rendez-vous en ligne',
    text: 'Chaque salon a sa page de réservation. La cliente choisit un créneau libre, un rappel limite les oublis.' },
  { k: 'shop', cls: 'lp-f', ico: I.bag(24), title: 'Boutique en ligne',
    text: 'Vendez shampoings, soins et accessoires sur une page à vous. Le stock se met à jour à chaque commande.' },
  { k: 'abo', cls: 'lp-f lp-f-soft', ico: I.card(24), title: 'Abonnements et fidélité',
    text: 'Proposez des forfaits de passages et des cartes de fidélité. Vous savez qui est abonnée et jusqu\'à quand.' },
  { k: 'cli', cls: 'lp-f', ico: I.user(24), title: 'Fichier clients',
    text: 'Historique des visites, couleur habituelle, longueur, notes et coordonnées. Vous retrouvez tout en deux secondes.' },
  { k: 'point', cls: 'lp-f lp-f-soft', ico: I.clock(24), title: 'Pointage de l\'équipe',
    text: 'Une borne à l\'entrée, un code, et l\'arrivée de chaque coiffeuse est enregistrée. Les heures sont prêtes en fin de mois.' },
  { k: 'avis', cls: 'lp-f', ico: I.star(24), title: 'Avis clients',
    text: 'Après la visite, la cliente reçoit un lien pour noter sa prestation. Les meilleurs avis s\'affichent sur votre page.' },
  { k: 'notif', cls: 'lp-f lp-f-dark', ico: I.bell(24), title: 'Notifications en temps réel',
    text: 'Nouveau rendez-vous, annulation, commande : l\'information arrive sur votre écran à l\'instant, sans recharger.' },
];

const STEPS = [
  { t: 'Créez votre salon', d: 'Nom du salon, pays et devise. Votre compte est prêt en quelques minutes.' },
  { t: 'Ajoutez prestations et équipe', d: 'Tarifs de coupe, coloration, tresses ou barbier, vos produits et vos coiffeuses. Importez vos clientes si vous en avez déjà.' },
  { t: 'Encaissez et pilotez', d: 'Partagez votre lien de réservation, encaissez à la caisse et suivez votre chiffre d\'affaires jour après jour.' },
];

function CutLine() {
  return <div className="lp-cut" aria-hidden="true">{I.scissors(20)}</div>;
}

function Logo({ withLink }) {
  const inner = <><span className="lp-logo-mark">{I.scissors(18)}</span><span>Sama<b>Salon</b></span></>;
  return withLink
    ? <Link to="/" className="lp-logo" aria-label="SamaSalon, accueil">{inner}</Link>
    : <span className="lp-logo">{inner}</span>;
}

function Shot({ src, alt, url, lazy }) {
  return (
    <div className="lp-shot">
      <div className="lp-shot-bar" aria-hidden="true"><i /><i /><i /><span>{url}</span></div>
      <img src={src} alt={alt} width="1280" height="633" {...(lazy ? { loading: 'lazy' } : {})} />
    </div>
  );
}

function ContactForm() {
  const ph = getPlaceholders();
  const [form, setForm] = useState({ nom: '', email: '', telephone: '', message: '' });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');
  const [apiError, setApiError] = useState('');

  const set = (k) => (e) => {
    const v = k === 'telephone' ? filterPhone(e.target.value) : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.nom.trim()) errs.nom = 'Indiquez votre nom.';
    if (!isValidEmail(form.email)) errs.email = 'Entrez une adresse email valide.';
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setStatus('sending');
    setApiError('');
    try {
      await api.post('/contact', {
        nom: form.nom.trim(),
        email: form.email.trim(),
        telephone: form.telephone.trim(),
        message: form.message.trim(),
      });
      setStatus('success');
    } catch (err) {
      setApiError(err?.response?.data?.message || 'L\'envoi a échoué. Vérifiez votre connexion et réessayez.');
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="lp-form lp-ok" role="status">
        <h3>Votre demande a bien été envoyée.</h3>
        <p>Merci {form.nom.trim()}. Nous vous répondons par email très vite pour organiser votre démonstration.</p>
      </div>
    );
  }

  return (
    <form className="lp-form" onSubmit={submit} noValidate aria-label="Demande de démonstration">
      {status === 'error' && <div className="lp-form-err" role="alert">{apiError}</div>}
      <div className="lp-field">
        <label htmlFor="lp-nom">Nom</label>
        <input id="lp-nom" name="nom" type="text" autoComplete="name" maxLength={100} value={form.nom} onChange={set('nom')}
          placeholder={ph.nomComplet} aria-invalid={!!errors.nom} aria-describedby={errors.nom ? 'lp-nom-err' : undefined} />
        {errors.nom && <div className="lp-err" id="lp-nom-err">{errors.nom}</div>}
      </div>
      <div className="lp-field">
        <label htmlFor="lp-email">Email</label>
        <input id="lp-email" name="email" type="email" autoComplete="email" value={form.email} onChange={set('email')}
          placeholder={ph.email} aria-invalid={!!errors.email} aria-describedby={errors.email ? 'lp-email-err' : undefined} />
        {errors.email && <div className="lp-err" id="lp-email-err">{errors.email}</div>}
      </div>
      <div className="lp-field">
        <label htmlFor="lp-tel">Téléphone <small>(facultatif)</small></label>
        <input id="lp-tel" name="telephone" type="tel" autoComplete="tel" value={form.telephone} onChange={set('telephone')}
          placeholder={ph.telephone} />
      </div>
      <div className="lp-field">
        <label htmlFor="lp-msg">Message <small>(facultatif)</small></label>
        <textarea id="lp-msg" name="message" maxLength={2000} value={form.message} onChange={set('message')}
          placeholder="Parlez-nous de votre salon : nombre de coiffeuses, devise, besoins." />
      </div>
      <button type="submit" className="lp-btn lp-btn-primary" disabled={status === 'sending'}>
        {status === 'sending' ? 'Envoi en cours' : 'Demander une démo'}
      </button>
    </form>
  );
}

export default function HomePage() {
  return (
    <div className="lp">
      <style>{CSS}</style>

      <header className="lp-hero">
        <div className="lp-wrap">
          <div className="lp-nav">
            <Logo withLink />
            <nav className="lp-nav-links" aria-label="Navigation principale">
              <a className="hide-sm" href="#fonctionnalites">Fonctionnalités</a>
              <a className="hide-sm" href="#contact">Démo</a>
              <Link to="/login">Se connecter</Link>
            </nav>
          </div>
          <div className="lp-hero-grid">
            <div>
              <h1 id="lp-h1">Votre salon de coiffure, enfin sous contrôle.</h1>
              <p className="lp-lead">
                De la coupe à l'encaissement : SamaSalon réunit les rendez-vous en ligne, la caisse, les finances,
                la boutique et vos clientes au même endroit. Depuis votre téléphone, votre tablette ou votre ordinateur.
              </p>
              <div className="lp-cta">
                <Link to="/inscription" className="lp-btn lp-btn-primary">Créer mon salon</Link>
                <Link to="/login" className="lp-btn lp-btn-ghost">Se connecter</Link>
              </div>
              <p className="lp-hero-note">{I.coins(16)} Pour les salons qui facturent en francs CFA ou en euros.</p>
            </div>
            <Shot src="/assets/landing/dashboard-finances.png" alt="Tableau de bord Finances de SamaSalon : courbe du chiffre d'affaires" url="samasalon.com/dashboard" />
          </div>
        </div>
      </header>

      <main>
        <section className="lp-sec" aria-labelledby="lp-about">
          <div className="lp-wrap lp-about">
            <div><span className="lp-snip">{I.scissors(26)}</span><h2 id="lp-about">C'est quoi SamaSalon ?</h2></div>
            <div>
              <p>
                <strong>SamaSalon est le logiciel de gestion de votre salon de coiffure.</strong> Il remplace le cahier de
                rendez-vous, la calculette et les feuilles de caisse par un seul outil.
              </p>
              <p>
                Vos clientes réservent en ligne, vous encaissez en quelques secondes, votre équipe pointe ses heures
                et vous voyez le chiffre d'affaires du jour sans rien recompter.
              </p>
            </div>
          </div>
        </section>

        <div className="lp-wrap"><CutLine /></div>

        <section className="lp-sec" style={{ paddingTop: 36 }} aria-labelledby="lp-apercu">
          <div className="lp-wrap">
            <span className="lp-snip">{I.comb(26)}</span>
            <h2 id="lp-apercu">SamaSalon en images</h2>
            <p className="lp-sec-intro">L'application telle que vous l'utiliserez au quotidien dans le salon.</p>
            <div className="lp-shots">
              <figure className="lp-big">
                <Shot lazy src="/assets/landing/dashboard-finances.png" alt="Écran Finances de SamaSalon avec la courbe du chiffre d'affaires" url="Finances" />
                <figcaption>Vos finances en un coup d'oeil<small>Chiffre d'affaires, dépenses et résultat, jour après jour.</small></figcaption>
              </figure>
              <figure>
                <Shot lazy src="/assets/landing/dashboard-caisse.png" alt="Écran Caisse de SamaSalon pour encaisser les prestations et les produits" url="Caisse" />
                <figcaption>La caisse<small>Encaissez une prestation en quelques gestes.</small></figcaption>
              </figure>
              <figure>
                <Shot lazy src="/assets/landing/dashboard-planning.png" alt="Écran Planning de SamaSalon avec les rendez-vous de l'équipe" url="Planning" />
                <figcaption>Le planning des rendez-vous<small>Toute l'équipe et tous les créneaux sur un seul écran.</small></figcaption>
              </figure>
            </div>
          </div>
        </section>

        <div className="lp-wrap"><CutLine /></div>

        <section className="lp-sec" id="fonctionnalites" style={{ paddingTop: 36 }} aria-labelledby="lp-feat">
          <div className="lp-wrap">
            <span className="lp-snip">{I.scissors(26)}</span>
            <h2 id="lp-feat">Du premier rendez-vous au dernier coup de ciseaux</h2>
            <p className="lp-sec-intro">Chaque outil parle aux autres : une vente en caisse met à jour vos stats, un rendez-vous pris en ligne prévient la coiffeuse.</p>

            <div className="lp-feats">
              {FEATURES.map((f) => (
                <article key={f.k} className={f.cls}>
                  <div className="lp-f-ico">{f.ico}</div>
                  <h3>{f.title}</h3>
                  <p>{f.text}</p>
                  {f.chips && (
                    <div className="lp-chips" aria-label="Moyens de paiement">
                      {f.chips.map((c) => <span key={c}>{c}</span>)}
                    </div>
                  )}
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="lp-sec lp-night" aria-labelledby="lp-markets">
          <div className="lp-wrap">
            <span className="lp-snip">{I.coins(26)}</span>
            <h2 id="lp-markets">À l'aise en francs CFA comme en euros</h2>
            <p className="lp-sec-intro">Que vous encaissiez en francs CFA ou en euros, SamaSalon parle la monnaie de votre salon dès la création du compte.</p>
            <div className="lp-markets">
              <div className="lp-market">
                <div className="lp-market-h">{I.coins(26)}<h3>Salons en francs CFA</h3></div>
                <p>Pour toute la zone FCFA : Sénégal, Côte d'Ivoire, Mali, Bénin et au-delà.</p>
                <dl>
                  <dt>Montants en FCFA</dt>
                  <dd>Prix, caisse et rapports en francs CFA, sans décimales.</dd>
                  <dt>Wave et Orange Money</dt>
                  <dd>Encaissez en espèces, par Wave ou par Orange Money, directement à la caisse.</dd>
                </dl>
              </div>
              <div className="lp-market">
                <div className="lp-market-h">{I.euro(26)}<h3>Salons en euros</h3></div>
                <p>Pour la France et le reste de la zone euro.</p>
                <dl>
                  <dt>Montants en euros</dt>
                  <dd>Prix, caisse et rapports en euros, centimes compris.</dd>
                  <dt>Espèces et carte</dt>
                  <dd>Les moyens de paiement de la caisse correspondent à ceux d'un salon de la zone euro.</dd>
                </dl>
              </div>
            </div>
          </div>
        </section>

        <section className="lp-sec" aria-labelledby="lp-steps">
          <div className="lp-wrap">
            <span className="lp-snip">{I.scissors(26)}</span>
            <h2 id="lp-steps">Comment ça marche</h2>
            <ol className="lp-steps">
              {STEPS.map((s, i) => (
                <li key={s.t}>
                  <span className="lp-step-n" aria-hidden="true">{i + 1}</span>
                  <div><h3>{s.t}</h3><p>{s.d}</p></div>
                </li>
              ))}
            </ol>
            <div className="lp-cta">
              <Link to="/inscription" className="lp-btn lp-btn-primary">Créer mon salon</Link>
            </div>
          </div>
        </section>

        <section className="lp-sec lp-contact" id="contact" aria-labelledby="lp-contact">
          <div className="lp-wrap lp-contact-grid">
            <div>
              <span className="lp-snip">{I.comb(26)}</span>
              <h2 id="lp-contact">Parlons de votre salon</h2>
              <p className="lp-sec-intro">
                Vous voulez voir SamaSalon avant de vous lancer ? Laissez-nous vos coordonnées. Nous vous proposons une démonstration adaptée à votre activité.
              </p>
            </div>
            <ContactForm />
          </div>
        </section>
      </main>

      <footer className="lp-foot">
        <div className="lp-wrap lp-foot-in">
          <div>
            <Logo />
            <p>La gestion de salon de coiffure, simple et complète.</p>
          </div>
          <nav aria-label="Pied de page">
            <Link to="/login">Connexion</Link>
            <Link to="/inscription">Créer mon salon</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}
